/**
 * "Build precedent pack" is a Workbench-only action, not a shared-contract
 * entity: it assembles an on-demand brief from data already resolvable via
 * real endpoints (the claim candidate's finding + precedent chain +
 * authority statuses, its matched clause, and its originating
 * disallowance) rather than one dedicated backend endpoint - there is
 * nothing on the backend for this to call directly, so it composes the
 * real calls this app already has.
 */
import { formatForum, formatIssueTag } from "../lib/format";
import { getAgreementClause } from "./agreementClauses";
import { getClaimCandidate } from "./claimCandidates";
import { getCompanyDisallowance } from "./companyDisallowances";
import type { PrecedentPack } from "./types";

export async function buildPrecedentPack(claimCandidateId: string): Promise<PrecedentPack> {
  const detail = await getClaimCandidate(claimCandidateId);
  const { claim_candidate: candidate, finding, precedent_findings: precedentFindings, authority_statuses } = detail;

  const statusByFinding = new Map(authority_statuses.map((entry) => [entry.finding_id, entry]));

  const [clauseDetail, disallowance] = await Promise.all([
    candidate.matched_clause_id ? getAgreementClause(candidate.matched_clause_id).catch(() => null) : null,
    candidate.disallowance_id ? getCompanyDisallowance(candidate.disallowance_id).catch(() => null) : null,
  ]);

  const precedentLines = precedentFindings.map((entry) => {
    const status = statusByFinding.get(entry.id);
    return `${entry.case_number} (${formatForum(entry.forum)}, ${entry.decision_date}) — ${entry.outcome}, authority status: ${status?.status ?? "unknown"}. ${entry.ratio_summary}`;
  });

  return {
    claim_candidate_id: candidate.id,
    generated_at: new Date().toISOString(),
    headline: `${formatIssueTag(candidate.issue_tag)} — ${finding.case_number}`,
    finding_summary: `${finding.case_number} (${formatForum(finding.forum)}, ${finding.decision_date}): ${finding.ratio_summary}`,
    precedent_lines: precedentLines,
    matched_clause_summary: clauseDetail
      ? `${clauseDetail.clause.clause_ref} — ${clauseDetail.agreement.name}: "${clauseDetail.clause.text}"`
      : null,
    disallowance_summary: disallowance
      ? `${disallowance.petition_ref}: claimed ${disallowance.amount_claimed.toLocaleString("en-IN")} / allowed ${disallowance.amount_allowed.toLocaleString("en-IN")} — ${disallowance.disallowance_reason}`
      : null,
    value_basis: candidate.value_basis,
    recommended_forum: candidate.recommended_forum,
    limitation_note: candidate.limitation_note,
    draft_argument: candidate.draft_argument,
    citation_count: precedentFindings.length,
  };
}
